import React from 'react';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import {
  Typography,
  Divider,
  Button,
  TextField,
  Box,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {CutCopyPasteTextField} from '../CommonComponents/CustomInputs';
import {RequestStatusId} from '../../ApplicationConfig/constants';
import AlertDialog from './AlertDialog';
import _ from "lodash";
import { useTranslation } from "react-i18next";
import {formatDate} from '../../Utilities/functions';

const PREFIX = 'ResearcherInfo';

const classes = {
  autocompleteOption: `${PREFIX}-autocompleteOption`
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')(() => ({
  [`& .${classes.autocompleteOption}`]: {
    whiteSpace: 'normal',
    wordBreak: 'break-all',
  }
}));

export default function ResearcherInfo(props) {

  const {t,i18n} = useTranslation();
  const [alertState, setAlertState] = React.useState({
    open:false,
    fn: null,
    message: ""
  });

  const handleRemoveErrors=(e)=>{
    if(Boolean(props.errors.researcherInformation?.[e.target.name]) && e.target.value!==""){
      let errs = _.cloneDeep(props.errors);
      delete errs.researcherInformation[e.target.name];
      props.setErrors(errs);
    }else if(Boolean(props.errors.filesList?.[e.target.name]) && e.target.value!==""){
      let errs = _.cloneDeep(props.errors);
      delete errs.filesList[e.target.name];
      props.setErrors(errs);
    }
  }

  const handleValueChange = (e)=>{
    props.setFormData({
      ...props.formData,
      [e.target.name]: e.target.value
    });

    handleRemoveErrors(e);
  };

  const handleRequestNameOnblur=(e)=>{
    if(e.target.value==""){
      handleValueChange({target:{name:e.target.name, value:t('dialog.newRequestDefaultTitle')}})
    }
  }

  const handleOutputFolderChange = (e)=> {
    props.setFormData({
      ...props.formData,
      outputFilesFolder: e.target.value
    });
    props.updateFilesList(e.target.value, props.fileFolders.filesObject)
    handleRemoveErrors(e);
  };

  const handleOutputFolderReset = (e, newData)=> {
    newData.outputFilesFolder=e.target.value;
    props.setFormData(newData);
    props.updateFilesList(e.target.value, props.fileFolders.filesObject);
    handleRemoveErrors(e);
    setAlertState({
      open:false,
      fn:null,
      message:""
    });
  };

  const handleOutputFolderAlertOpen = (e)=> {
    if(e.target.value !== "" && props.formData.outputFilesFolder !== ""){
      let newData = _.cloneDeep(props.formData);
      newData.outputFiles = [];

      Object.values(newData.supportingFiles).forEach(files=>{
        files.forEach(file=>{
          file.fileName="";
          file.notes="";
        });
      });
      
      if(!_.isEqual(props.formData, newData)){
        newData.outputFilesFolder=e.target.value;
        setAlertState({
          open:true,
          fn:()=>handleOutputFolderReset(e, newData),
          message:t("requestForm.filesList.areYouSureOutput")
        });
      }else{
        handleOutputFolderChange(e);
      }
    }else{
      handleOutputFolderChange(e);
    }
  };
  
  return (
    (<Root>
      <Typography component="h2" variant="h6" sx={{mb: 3}}>
        {t("requestForm.researcherInformation.requestDetails")}
      </Typography>
      <Typography component="p" variant="body1" sx={{mb: 3}}>
        {t("requestForm.researcherInformation.description")}
      </Typography>
      <Divider sx={{mb: 3}} />
      <Typography component="h3" variant="subtitle2" sx={{mb: 3}}>
      {t("requestForm.researcherInformation.details")}
      </Typography>
      <Box sx={{mb: 3}}>
        <CutCopyPasteTextField
          id="project"
          name="project"
          label={t("requestForm.researcherInformation.project")}
          variant="outlined"
          required
          fullWidth
          margin="dense"
          
          inputProps={{readOnly: true}}
          value={props.request.contractNumber}
        />
      </Box>
      <Box sx={{mb: 3}}>
        <CutCopyPasteTextField
          id="requestId"
          name="requestId"
          label={t("requestForm.researcherInformation.requestId")}
          variant="outlined"
          required
          fullWidth
          margin="dense"
          inputProps={{readOnly: true}}
          value={props.request.identifier}
        />
      </Box>
      <Autocomplete
        sx={{mb: 3}}        
        componentsProps={{
          clearIndicator: {tabIndex: 0},
        }}
        classes={{
          option: classes.autocompleteOption,
        }}
        id="outputFilesFolder"
        options={props.fileFolders.foldersList}
        renderInput={(params) => {
          let readOnlyParam = props.readOnly  || props.analystEditFlag || props.request.status.id !== RequestStatusId.Draft ?
            {InputProps: {readOnly: true}} : {};
          return (
            <TextField
              {...params}
              multiline
              minRows="1"
              label={t("requestForm.filesList.requestFolder")}
              required={true}
              variant="outlined"
              error={Boolean(t(props.errors.filesList?.outputFilesFolder))}
              helperText={t(props.errors.filesList?.outputFilesFolder)}
              {...readOnlyParam}
          />
        );}}
        value={props.formData.outputFilesFolder==="" ? null : props.formData.outputFilesFolder}
        onChange={(event, newValue) => {
          if(newValue===null){
            newValue="";
          }
          handleOutputFolderAlertOpen({target: {name: "outputFilesFolder", value: newValue}});
        }}
        disableClearable={true}
      />
      { props.request.requestReviewFolderName.length > 0 &&
            <Box sx={{mb: 3}}>
              <CutCopyPasteTextField 
                id="requestReviewFolderName"
                name="requestReviewFolderName"
                label={t("requestForm.requestReviewFolderName")}
                variant="outlined"
                fullWidth
                required
                margin="dense"
                value={props.request.requestReviewFolderName}
                inputProps={{readOnly: props.readOnly}}
              />
          </Box>
      }

      <Box sx={{mb: 3}}>
        <CutCopyPasteTextField 
          id="requestName"
          name="requestName"
          label={t("requestForm.researcherInformation.requestName")}
          variant="outlined"
          fullWidth
          required
          margin="dense"
          value={props.formData.requestName}
          onChange={handleValueChange}
          onBlur={handleRequestNameOnblur}
          inputProps={{readOnly: props.readOnly}}
          error={Boolean(props.errors.researcherInformation?.requestName)}
          helperText={t(props.errors.researcherInformation?.requestName)}
        />
      </Box>
      <Box sx={{mb: 3}}>
        <CutCopyPasteTextField
          id="createdDate"
          name="createdDate"
          label={t("requestForm.researcherInformation.createdOn")}
          variant="outlined"
          required
          fullWidth
          margin="dense"
          value={formatDate(props.request.createdOn,i18n.language,true)}
          InputProps={{readOnly: true}}
        />
      </Box>
      <Box sx={{mb: 3}}>
        <CutCopyPasteTextField
          id="updatedDate"
          name="updatedDate"
          label={t("requestForm.researcherInformation.updatedOn")}
          variant="outlined"
          required
          fullWidth
          margin="dense"
          value={formatDate(props.request.updatedOn,i18n.language,true)}
          InputProps={{readOnly: true}}
        />
      </Box>
      <Divider sx={{mb: 3}} />
      <Typography component="h3" variant="subtitle2" sx={{mb: 3}}>
        {t("requestForm.researcherInformation.requester")}
      </Typography>
      <Box sx={{mb: 3}}>
        <CutCopyPasteTextField
          id="firstName"
          name="firstName"
          label={t("requestForm.researcherInformation.firstName")}
          variant="outlined"
          required
          fullWidth
          margin="dense"
          value={props.request.researcher.firstName}
          InputProps={{readOnly: true}}
        />
      </Box>
      <Box sx={{mb: 3}}>
        <CutCopyPasteTextField
          id="lastName"
          name="lastName"
          label={t("requestForm.researcherInformation.lastName")}
          variant="outlined"
          required
          fullWidth
          margin="dense"
          value={props.request.researcher.lastName}
          InputProps={{readOnly: true}}
        />
      </Box>
      <Box sx={{mb: 3}}>
        <CutCopyPasteTextField
          id="username"
          name="username"
          label={t("requestForm.researcherInformation.username")}
          variant="outlined"
          required
          fullWidth
          margin="dense"
          value={props.request.researcher.account}
          InputProps={{readOnly: true}}
        />
      </Box>
      {/*<Box sx={{mb: 3}}>
        <CutCopyPasteTextField
          id="role"
          name="role"
          label={t("requestForm.researcherInformation.role")}
          variant="outlined"
          required
          fullWidth
          margin="dense"
          value={"Researcher"}
          InputProps={{readOnly: true, rows:{max: 0}}}
        />
      </Box>*/}
      <Box sx={{mb: 3}}>
        <CutCopyPasteTextField
          id="email"
          name="email"
          label={t("requestForm.researcherInformation.email")}
          variant="outlined"
          required
          fullWidth
          margin="dense"
          value={props.request.researcher.email}
          InputProps={{readOnly: true}}
        />
      </Box>
      <AlertDialog
        openAlert={alertState.open}
        handleAlertClose={()=>setAlertState({
          open:false,
          fn:null,
          message:""
        })}
        titleMessage={t("requestForm.areYouSure")}
        descriptionMessage={alertState.message}
      >
        <Button
          onClick={()=>setAlertState({
            open:false,
            fn:null,
            message:""
          })}
          color="primary"
          variant="outlined"
        >
          {t("requestForm.no")}
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={alertState.fn}
        >
          {t("requestForm.yes")}
        </Button>
      </AlertDialog>
    </Root>)
  );
}
ResearcherInfo.propTypes={
  formData: PropTypes.object.isRequired,
  setFormData: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  setErrors: PropTypes.func.isRequired,
  readOnly: PropTypes.bool.isRequired,
  request: PropTypes.object.isRequired,
  analystEditFlag: PropTypes.bool.isRequired,
  fileFolders: PropTypes.object.isRequired,
  updateFilesList: PropTypes.func.isRequired,
};